import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { CanAccess, useInvalidate, useNavigation } from '@refinedev/core';

import {
  Button, Chip, Loading, useSnackbar,
} from '@rrexpress/core';
import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'lodash';

import ActionDelete from '@/components/action-button/delete';
import ActionUpdate from '@/components/action-button/update';
import BaseList from '@/components/base-list';
import { useCanPerform } from '@/hooks/useCanPerform';
import useLayout from '@/hooks/useLayout';
import { useModal } from '@/hooks/useModal';
import useContact from '@/modules/contact/application/contact';
import { type IContact } from '@/modules/contact/model/contact';

const Contact = () => {
  useLayout({ title: 'Kontak' });
  const { edit, create } = useNavigation();
  const { open, close } = useModal();

  const { deleteContact, loadingDelete } = useContact();
  const { show } = useSnackbar();
  const invalidate = useInvalidate();

  const canPerform = useCanPerform('contact');

  async function handleOnDelete(id: string, rowVersion: string) {
    try {
      await deleteContact(id, rowVersion);
      close();
      show('Berhasil menghapus kontak', 'success');
      invalidate({ resource: 'contacts', invalidates: ['list'] });
    } catch (error) {
      show('Gagal menghapus kontak', 'error');
    }
  }

  const columns: ColumnDef<IContact>[] = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'contact_name',
      header: 'Nama Kontak',
      cell: ({ row }) => (
        <Link to={`/contacts/show/${row.original.contact_id}`}>
          <span className="text-blue-500">{row.original.contact_name}</span>
        </Link>
      ),
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'Email',
    },
    {
      id: 'phone_number',
      accessorKey: 'phone_number',
      header: 'No. Telp',
    },
    {
      id: 'city',
      accessorKey: 'address_city_name',
      header: 'Kota',
    },
    {
      id: 'is_active',
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => (
        <Chip
          label={getValue() ? 'Aktif' : 'Tidak Aktif'}
          backgroundColor={getValue() ? '#E6F7F4' : '#DFE0E9'}
          borderColor={getValue() ? '#E6F7F4' : '#DFE0E9'}
          color={getValue() ? '#006C5A' : '#888888'}
        />
      ),
    },
    {
      id: 'action',
      accessorKey: 'contact_id',
      header: 'Action',
      cell: ({ getValue, row }) => (
        <div className="flex flex-row gap-x-2">
          <CanAccess resource="contacts" action="edit">
            <ActionUpdate onClick={() => edit('contacts', getValue() as string)} />
          </CanAccess>
          <CanAccess resource="contacts" action="delete">
            <ActionDelete
              onClick={() => open({
                title: 'Hapus Kontak',
                template: loadingDelete
                  ? (
                    <Loading
                      variant="circular"
                      size={40}
                      speed={0.6}
                      indicatorCap="round"
                    />
                  )
                  : (
                    <p className="text-[#222222]">
                      Apakah anda yakin akan menghapus <span className="font-medium">{row?.original?.contact_name}</span> dari daftar kontak?
                    </p>
                  ),
                footer: (
                  <div className="flex gap-3">
                    <Button variant="text" onClick={() => close()}>Batal</Button>
                    <Button backgroundColor="#BE321E" onClick={() => handleOnDelete(getValue() as string, row.original.row_version)}>
                      Hapus
                    </Button>
                  </div>
                ),
              })}
            />
          </CanAccess>
        </div>
      ),
    },
  ], []);

  return (
    <BaseList
      resource="contacts"
      title="Daftar Kontak"
      columns={columns as any}
      filter={{
        sections: [
          {
            layout: {
              direction: 'horizontal',
              sections: [
                {
                  input: {
                    name: 'type',
                    type: 'dropdown',
                    label: 'Type',
                    comboboxOptions: [
                      { label: 'Nama Kontak', value: 'name' },
                      { label: 'Email', value: 'email' },
                      { label: 'No. Telp', value: 'phone_number' },
                    ],
                  },
                },
                {
                  input: {
                    name: 'keyword',
                    type: 'textfield',
                    label: 'Keyword',
                  },
                },
                {
                  input: {
                    type: 'dropdown',
                    name: 'is_active',
                    label: 'Aktif',
                    defaultValue: 'all',
                    comboboxOptions: [
                      {
                        label: 'Semua',
                        value: 'all',
                      },
                      {
                        label: 'Aktif',
                        value: 'true',
                      },
                      {
                        label: 'Tidak Aktif',
                        value: 'false',
                      },
                    ],
                  },
                },
                {
                  input: {
                    type: 'queryfield',
                    name: 'address_city_id',
                    label: 'Kota',
                    comboboxResource: 'cities',
                    queryfieldKeyOptionLabel: 'city_name',
                    queryfieldKeyOptionValue: 'city_id',
                    queryfieldKeyQueryFilter: 'city_name',
                    dataProviderName: 'masterdata',
                  },
                },
              ],
            },
          },
        ],
        onSubmit: (rawFilterData) => {
          if (!isEmpty(rawFilterData)) {
            const newFilter = { ...rawFilterData };

            if (newFilter?.is_active === 'true') newFilter.is_active = true;
            if (newFilter?.is_active === 'false') newFilter.is_active = false;
            if (newFilter?.is_active === 'all') newFilter.is_active = undefined;

            return {
              ...newFilter,
              contact_name: newFilter.type === 'name' && newFilter.keyword ? newFilter.keyword : null,
              email: newFilter.type === 'email' && newFilter.keyword ? newFilter.keyword : null,
              phone_number: newFilter.type === 'phone_number' && newFilter.keyword ? newFilter.keyword : null,
            };
          }
          return rawFilterData;
        },
      }}
      headerActions={[
        {
          type: 'tertiary',
          label: 'Tambah Baru',
          onClick: () => {
            if (canPerform.create) create('contacts');
          },
        },
      ]}
    />
  );
};

export default Contact;
